import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Stack,
  Box,
  Avatar,
  Fade,
  Grid,
} from '@mui/material';
import { Inventory, QrCode } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ProductCardProps } from './types';
import { ProductLanguageChips } from './ProductLanguageChips';

const getProductInitials = (name: string): string => {
  return name
    .split(' ')
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

export const ProductCard: React.FC<ProductCardProps> = ({ product, onView }) => {
  const { t } = useTranslation();

  return (
    <Grid item xs={12} sm={6} lg={4}>
      <Fade in timeout={220}>
        <Card
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            cursor: 'pointer',
            transition: 'border-color 0.2s ease, background-color 0.2s ease',
            '&:hover': {
              borderColor: 'primary.main',
              backgroundColor: 'action.hover',
            },
          }}
          onClick={() => onView(product.id)}
        >
          <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start" mb={2}>
              <Avatar
                sx={{
                  bgcolor: 'primary.main',
                  width: 42,
                  height: 42,
                  fontSize: '0.875rem',
                  fontWeight: 600,
                }}
              >
                {getProductInitials(product.name)}
              </Avatar>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    mb: 0.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {product.name}
                </Typography>
                <ProductLanguageChips
                  languages={product.translation_languages}
                  baseLanguage={product.base_language}
                  maxVisible={4}
                />
              </Box>
            </Stack>

            <Stack spacing={1}>
              {product.sku && (
                <Box display="flex" alignItems="center" gap={1}>
                  <Inventory sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    <strong>{t('products.sku')}:</strong> {product.sku}
                  </Typography>
                </Box>
              )}
              {product.ean && (
                <Box display="flex" alignItems="center" gap={1}>
                  <QrCode sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    <strong>{t('products.ean')}:</strong> {product.ean}
                  </Typography>
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Fade>
    </Grid>
  );
};

